import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pacienteService } from '../services';
import useDebounce from '../hooks/useDebounce';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  AlertTriangle,
  Users,
  User,
  FileText
} from 'lucide-react';

const Pacientes = () => {
  const { user } = useAuth();
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSearching, setIsSearching] = useState(false);

  // Aplicar debounce na pesquisa com 500ms de delay
  const debouncedSearch = useDebounce(search, 500);

  const carregarPacientes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: debouncedSearch
      };
      
      const response = await pacienteService.getAll(params);
      setPacientes(response.pacientes || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar pacientes' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    carregarPacientes();
  }, [carregarPacientes]);

  const handleDelete = async (id, nome) => {
    if (user.cargo !== 'admin') {
      setMessage({ type: 'error', text: 'Apenas administradores podem excluir pacientes' });
      return;
    }

    if (window.confirm(`Tem certeza que deseja excluir o paciente "${nome}"?`)) {
      try {
        await pacienteService.delete(id);
        setMessage({ type: 'success', text: 'Paciente excluído com sucesso' });
        carregarPacientes();
      } catch (error) {
        console.error('Erro ao excluir paciente:', error);
        setMessage({ type: 'error', text: 'Erro ao excluir paciente' });
      }
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    if (e.target.value !== debouncedSearch) {
      setIsSearching(true);
    }
  };

  // Resetar página quando o debouncedSearch muda
  useEffect(() => {
    setCurrentPage(1);
    setIsSearching(false);
  }, [debouncedSearch]);

  const formatarCpf = (cpf) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatarCartaoSus = (cartao) => {
    if (!cartao) return '-';
    return cartao.replace(/(\d{3})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users size={32} />
            Pacientes
          </h1>
          <p style={{ color: '#667', margin: '5px 0 0 0' }}>
            Gerencie o cadastro de pacientes
          </p>
        </div>
        
        <Link to="/pacientes/novo" className="btn btn-primary">
          <Plus size={20} />
          Novo Paciente
        </Link>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.text}
        </div>
      )}

      <div className="card">
        {/* Busca */}
        <div className="search-box">
          <div className="search-input" style={{ position: 'relative' }}>
            <Search size={20} style={{ 
              position: 'absolute', 
              left: '10px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: isSearching ? '#007bff' : '#667'
            }} />
            {isSearching && (
              <div style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#007bff',
                fontSize: '12px'
              }}>
                Buscando...
              </div>
            )}
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nome, CPF ou cartão SUS..."
              value={search}
              onChange={handleSearch}
              style={{ 
                paddingLeft: '40px',
                paddingRight: isSearching ? '80px' : '10px'
              }}
            />
          </div>
        </div>

        {/* Tabela */}
        {pacientes.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>CPF</th>
                    <th>Cartão SUS</th>
                    <th>RG</th>
                    <th>Endereço</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pacientes.map((paciente) => (
                    <tr key={paciente.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <User size={16} color="#667" />
                          <strong>{paciente.nome}</strong>
                        </div>
                      </td>
                      <td style={{ fontFamily: 'monospace' }}>
                        {formatarCpf(paciente.cpf)}
                      </td>
                      <td style={{ fontFamily: 'monospace' }}>
                        {formatarCartaoSus(paciente.cartaoSus)}
                      </td>
                      <td>{paciente.rg || '-'}</td>
                      <td style={{ maxWidth: '200px' }}>
                        {paciente.endereco ? (
                          <div style={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            fontSize: '12px',
                            color: '#667'
                          }}>
                            {paciente.endereco}
                          </div>
                        ) : '-'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Link 
                            to={`/pacientes/editar/${paciente.id}`}
                            className="btn btn-secondary"
                            style={{ padding: '6px 10px' }}
                          >
                            <Edit size={16} />
                          </Link>
                          <Link 
                            to={`/pacientes/detalhes/${paciente.id}`}
                            className="btn btn-secondary"
                            style={{ padding: '6px 10px' }}
                          >
                            <FileText size={16} />
                          </Link>
                          {user.cargo === 'admin' && (
                            <button
                              onClick={() => handleDelete(paciente.id, paciente.nome)}
                              className="btn btn-danger"
                              style={{ padding: '6px 10px' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                
                <span style={{ margin: '0 20px', color: '#667' }}>
                  Página {currentPage} de {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px',
            color: '#667'
          }}>
            <Users size={48} style={{ opacity: 0.5, marginBottom: '15px' }} />
            <h3>Nenhum paciente encontrado</h3>
            <p>
              {search 
                ? 'Tente ajustar o termo de busca' 
                : 'Comece cadastrando o primeiro paciente'
              }
            </p>
            {!search && (
              <Link to="/pacientes/novo" className="btn btn-primary" style={{ marginTop: '15px' }}>
                <Plus size={20} />
                Cadastrar Primeiro Paciente
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Pacientes;